import { format, parse } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { FETCH_JOB_ACTION, fetchJob } from '~/actions';
import { convertMinutesToFormattedValue } from '~/components/Request/multiday/utils';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';
import { useToggle } from '~/hooks';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useHasValidPaymentMethod } from '~/hooks/useHasValidPaymentMethod';
import useLoading from '~/hooks/useLoading';
import { useNavigationDetector } from '~/hooks/useNavigationDetector';
import {
  useEditOpenShiftMutation,
  type IEditOpenShiftMutationPayload,
} from './useEditOpenShiftMutation';

export interface IEditOpenShiftFormValues {
  professionType: string;
  preferredSpecialty: string;
  isAssistedHygiene?: boolean;
  arrivalTime: string;
  departureTime: string;
  mealBreakDuration: string;
  isMealBreakPaid: boolean;
  rate: number | null;
  procedures: string[];
  autoConfirm: { favorites: boolean; eligibleProfessionals: boolean };
}

const getDefaultValues = (job: any): IEditOpenShiftFormValues => ({
  professionType:
    UserSubProfession.getName(job.subProfession) ||
    UserProfession.getName(job.profession) ||
    job.profession,
  preferredSpecialty: job.specialty || 'specialty_General Dentistry',
  isAssistedHygiene: job.isAssistedHygiene,
  arrivalTime: job.local_start_time?.toLowerCase(),
  departureTime: job.local_end_time?.toLowerCase(),
  mealBreakDuration: convertMinutesToFormattedValue(job.lunch_break_time),
  isMealBreakPaid: job.is_lunch_break_paid || false,
  rate: job.rate != null ? Math.round(job.rate) : null,
  procedures: job.procedures || [],
  autoConfirm: {
    favorites: job.autofillOptions?.favorite || false,
    eligibleProfessionals: job.autoFill || false,
  },
});

const getInitialDate = (job: any): Date =>
  parse(job.local_date, 'EEE, MMM dd, yyyy', new Date());

export const useEditOpenShift = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const job = useAppSelector((state) => state.job.job);
  const { isLoading: isLoadingJob } = useLoading([FETCH_JOB_ACTION]);
  const selectedOfficeName = useAppSelector(
    (state) => state.user.selectedChildOffice?.office_name,
  );

  useEffect(() => {
    if (jobId && jobId !== job?.id) {
      dispatch(fetchJob({ jobId }));
    }
  }, [jobId, job?.id, dispatch]);

  // The owning office (offer_owner) drives both the displayed name and the rate
  // recommendation — not the globally selected office. The job detail payload
  // does not include a top-level office_name, so falling back to the selected
  // office would show the parent office instead of the shift's office.
  const officeName =
    job?.offer_owner?.office_name || job?.office_name || selectedOfficeName || '';
  const officeId = job?.offer_owner?.id ?? '';
  const professionCode = job ? UserProfession.getNormalizedCode(job.profession) : '';

  const { control, watch, getValues, setValue, reset, formState } =
    useForm<IEditOpenShiftFormValues>();

  // Auto-confirm requires a payable office. When the office can't pay, the
  // feature is locked: the field is disabled and any stored value is cleared.
  const locked = !useHasValidPaymentMethod(officeId);

  useEffect(() => {
    if (!locked) return;

    setValue(
      'autoConfirm',
      { favorites: false, eligibleProfessionals: false },
      { shouldDirty: false },
    );
  }, [locked, setValue]);

  const hasInitialized = useRef(false);
  const initialDateRef = useRef<string>('');
  useEffect(() => {
    if (job && !hasInitialized.current) {
      hasInitialized.current = true;
      reset(getDefaultValues(job));
      const jobDate = getInitialDate(job);
      setSelectedDate(jobDate);
      initialDateRef.current = jobDate.toISOString();
    }
  }, [job, reset]);

  const professionType = watch('professionType');
  const preferredSpecialty = watch('preferredSpecialty');

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const dateChanged =
    initialDateRef.current !== '' && selectedDate.toISOString() !== initialDateRef.current;
  const isDirty = formState.isDirty || dateChanged;

  const [openField, setOpenField] = useState<string | null>(null);
  const [showSuccessPopup, successPopupControls] = useToggle();
  const [showNavigationGuard, setShowNavigationGuard] = useState(false);

  const toggleField = useCallback((fieldName: string) => {
    setOpenField((current) => (current === fieldName ? null : fieldName));
  }, []);

  const onNavigationAttempt = useCallback(() => {
    setShowNavigationGuard(true);
  }, []);

  const { allowNavigation, navigateToPending, clearPendingNavigation } = useNavigationDetector({
    when: isDirty,
    onNavigationAttempt,
  });

  const mutation = useEditOpenShiftMutation({
    jobId: jobId!,
    professionCode: professionCode || '',
    onSuccess: () => {
      successPopupControls.setOn();
    },
  });

  const handleSave = useCallback(() => {
    const formValues = getValues();
    const payload: IEditOpenShiftMutationPayload = {
      localDate: format(selectedDate, 'yyyy-MM-dd'),
      arrivalTime: formValues.arrivalTime,
      departureTime: formValues.departureTime,
      mealBreakDuration: formValues.mealBreakDuration,
      isMealBreakPaid: formValues.isMealBreakPaid,
      rate: formValues.rate,
      procedures: formValues.procedures,
      isAssistedHygiene: formValues.isAssistedHygiene,
      // When the office can't pay, never persist an enabled auto-confirm —
      // guards against a stale value carried over from the loaded job.
      autoConfirmFavorites: locked ? false : formValues.autoConfirm.favorites,
      autoConfirmEligible: locked ? false : formValues.autoConfirm.eligibleProfessionals,
    };
    mutation.mutate(payload);
  }, [getValues, selectedDate, locked, mutation]);

  const handleSuccessConfirm = useCallback(() => {
    allowNavigation();
    navigate(-1);
  }, [allowNavigation, navigate]);

  const handleBack = useCallback(() => {
    if (!isDirty) {
      allowNavigation();
    }
    navigate(-1);
  }, [isDirty, allowNavigation, navigate]);

  return {
    job,
    isLoadingJob,
    officeName,
    officeId,
    professionType,
    preferredSpecialty,
    professionCode,
    locked,

    control,
    watch,

    selectedDate,
    setSelectedDate,

    openField,
    toggleField,
    showSuccessPopup,
    showNavigationGuard,

    isSaving: mutation.isPending,

    handleSave,
    handleSuccessConfirm,
    handleBack,

    navigateToPending,
    clearPendingNavigation,
    setShowNavigationGuard,
  };
};
